import { push } from '@lagunovsky/redux-react-router'
import { groupBy, minBy, orderBy } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { ALIGN, NewTable, WrapperCardContent } from '../../../components/datatable/NewTable'
import NumberField from '../../../components/forms/NumberField'
import Icon from '../../../components/icon/Icon'
import DtoHydrometricStation from '../../../hydrometry/dto/DtoHydrometricStation'
import DtoInstallation from '../../../installation/dto/installation/DtoInstallation'
import DtoPiezometer from '../../../piezometry/dto/DtoPiezometer'
import PiezometerAccessibilitieDto from '../../../piezometry/dto/PiezometerAccessibilitieDto'
import PluviometerDto from '../../../pluviometry/dto/PluviometerDto'
import { nbPerPageLabel } from '../../../referencial/constants/ReferencialConstants'
import { getEventsClosureModal } from '../../../utils/CampaignUtil'
import useListIndexed from '../../../utils/customHook/useListIndexed'
import { hasValue } from '../../../utils/NumberUtil'
import CampaignAction from '../../actions/CampaignAction'

const headers = ['code', 'city', 'name', 'contact', 'access', 'order', 'nullValue']

const ListStationCampaign = ({
    campaignId = -1,
    editMode = false,
    onDeleteStation = () => { },
    accessibilities = [],
    stationType = '',
    stations = [],
    onChangeOrder = () => { },
}) => {
    const {
        citiesIndex,
        contacts,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    const dispatch = useDispatch()

    const indexedContacts = useListIndexed(contacts)
    const accessByStation = useMemo(() => groupBy(accessibilities, 'id'), [accessibilities])
    const getCityName = code => citiesIndex[code]?.name ?? ''

    const getOrderItem = stationSelected => {
        const {
            id,
            order,
        } = stationSelected
        if (editMode) {
            return (
                <NumberField
                    value={order}
                    min={0}
                    onChange={v => onChangeOrder(id, v)}
                />
            )
        }
        return order
    }

    const eventsClosureModal = (events, label) => {
        const dateNow = moment().valueOf()
        getEventsClosureModal(events.filter(e =>
            !(e.closeDate && e.closeDate > dateNow),
        ), label, () => dispatch(CampaignAction.fetchCampaign(stationType, campaignId)))
    }

    const getMonitoringIcon = ({ monitoring, monitoringEvents }) => monitoring !== 0 && (
        <Icon
            icon='notifications'
            className='blue-text'
            tooltip={i18n.monitoredStation}
            onClick={() => campaignId && stationType === 'piezometry' && eventsClosureModal(monitoringEvents, `${i18n.events} "${i18n.toMonitor}"`)}
        />
    )

    const getPlanIcon = ({ toPlanEvents, toPlanEventLength }) => toPlanEventLength !== 0 && (
        <Icon
            icon='assignment'
            className='blue-text'
            tooltip={i18n.toPlan}
            onClick={() => campaignId && stationType === 'piezometry' && eventsClosureModal(toPlanEvents, `${i18n.events} "${i18n.toPlan}"`)}
        />
    )

    const getContactCell = contact => {
        if (!contact) {
            return null
        }
        const phone = contact.phoneTel || contact.mobile
        const postalCode = contact.postalCode ? `${contact.postalCode}, ` : ''
        return {
            value: contact.name,
            sortValue: contact.name,
            leftIcon: { icon: 'account_circle' },
            rightIcon: phone ? { icon: 'call' } : {},
            tooltip: (
                <>
                    <div>{contact.name}</div>
                    <div>{contact.address}</div>
                    <div>{postalCode + getCityName(contact.cityCode)}</div>
                    <div>{phone}</div>
                </>
            ),
        }
    }

    const getAccessCell = descriptions => {
        if (!descriptions.length) {
            return null
        }
        const [firstDescription] = descriptions
        return {
            value: firstDescription.length > 80 ? `${firstDescription.slice(0, 77)}...` : firstDescription,
            sortValue: firstDescription,
            leftIcon: { icon: 'directions' },
            tooltip: (
                <>
                    {descriptions.map((description, i) => <div key={i}>{description}</div>)}
                </>
            ),
        }
    }

    const selectedStations = stations.map(station => {
        const stationAccess = accessByStation[station.id] || []
        const access = stationAccess.length && stationAccess.find(a => hasValue(a.order)) ? minBy(stationAccess, 'order') : (stationAccess.length ? stationAccess[0] : null)
        const contact = indexedContacts[access && access.contactCode ? access.contactCode : station.contactCode]
        const descriptions = stationAccess.filter(a => a.description).map(a => a.description)
        return {
            id: station.id,
            monitoringEvents: station.monitoringEvents,
            toPlanEvents: station.toPlanEvents,
            nullValue: (
                <>
                    {getMonitoringIcon(station)}
                    {getPlanIcon(station)}
                </>
            ),
            code: {
                value: station.code,
                onClick: () => dispatch(push(`/station/${stationType}/${station.id}`)),
            },
            city: station.townCode ? `${getCityName(station.townCode)} [${station.townCode}]` : null,
            name: station.name,
            contact: getContactCell(contact),
            exportContact: contact ? [contact.name || '', contact.address, contact.postalCode, getCityName(contact.cityCode), contact.phoneTel || contact.mobile].filter(str => !!str).join(', ') : '',
            order: {
                value: getOrderItem(station),
                sortValue: station.order,
            },
            visitOrder: station.order,
            estimated: station.previsionalVisitNumber,
            access: getAccessCell(descriptions),
            exportAccess: orderBy(stationAccess, 'order').map(a => a.description).join(', '),
        }
    })
    return (
        <NewTable
            rows={orderBy(selectedStations, ['visitOrder', 'code.value'])}
            headers={headers}
            lineActions={[{
                icon: 'delete',
                tooltip: i18n.delete,
                onClick: onDeleteStation,
                displayed: editMode,
                align: ALIGN.RIGHT,
            }]}
            rowsPerPageOptions={nbPerPageLabel}
            WrapperComponent={WrapperCardContent}
        />
    )
}

ListStationCampaign.propTypes = {
    campaignId: PropTypes.number,
    editMode: PropTypes.bool,
    onDeleteStation: PropTypes.func,
    accessibilities: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.instanceOf(PiezometerAccessibilitieDto),
    ])),
    stationType: PropTypes.oneOf(['piezometry', 'pluviometry', 'hydrometry', 'installation']),
    stations: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.instanceOf(PluviometerDto)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometer)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoHydrometricStation)),
        PropTypes.arrayOf(PropTypes.instanceOf(DtoInstallation)),
    ]),
    onChangeOrder: PropTypes.func,
}

export default ListStationCampaign
